import React, { useState } from 'react';
import { Star, Send } from 'lucide-react';
import { submitReview } from '../services/reviews-api';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';

const ReviewForm = ({ onReviewSubmitted }) => {
    const { language } = useLanguage();
    const [formData, setFormData] = useState({
        customer_name: '',
        review_text: '',
        rating: 5,
        workshop_attended: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hoveredStar, setHoveredStar] = useState(0);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const newReview = await submitReview(formData);
            toast.success(language === 'pl' ? 'Dziękujemy za opinię!' : 'Thank you for your review!');

            // Reset form
            setFormData({
                customer_name: '',
                review_text: '',
                rating: 5,
                workshop_attended: ''
            });

            // Notify parent to refresh reviews
            if (onReviewSubmitted) {
                onReviewSubmitted(newReview);
            }
        } catch (error) {
            toast.error(language === 'pl' ? 'Nie udało się wysłać opinii' : 'Failed to submit review');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    return (
        <div className="bg-cream-white rounded-lg p-8 shadow-lg border border-soft-gray">
            <h3 className="font-serif text-3xl text-charcoal mb-6">
                {language === 'pl' ? 'Zostaw Opinię' : 'Leave a Review'}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name Input */}
                <div>
                    <label htmlFor="customer_name" className="block text-sm font-medium text-charcoal mb-2">
                        {language === 'pl' ? 'Twoje Imię' : 'Your Name'} *
                    </label>
                    <input
                        type="text"
                        id="customer_name"
                        name="customer_name"
                        value={formData.customer_name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-soft-gray rounded focus:ring-2 focus:ring-warm-sage focus:border-warm-sage transition-all"
                        placeholder={language === 'pl' ? 'Anna K.' : 'Anna K.'}
                    />
                </div>

                {/* Rating */}
                <div>
                    <label className="block text-sm font-medium text-charcoal mb-2">
                        {language === 'pl' ? 'Ocena' : 'Rating'} *
                    </label>
                    <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                            <button
                                key={star}
                                type="button"
                                onClick={() => setFormData({ ...formData, rating: star })}
                                onMouseEnter={() => setHoveredStar(star)}
                                onMouseLeave={() => setHoveredStar(0)}
                                className="transition-transform hover:scale-110"
                            >
                                <Star
                                    size={32}
                                    className={
                                        star <= (hoveredStar || formData.rating)
                                            ? 'fill-amber-400 text-amber-400'
                                            : 'text-soft-gray fill-transparent'
                                    }
                                />
                            </button>
                        ))}
                    </div>
                </div>

                {/* Workshop (Optional) */}
                <div>
                    <label htmlFor="workshop_attended" className="block text-sm font-medium text-charcoal mb-2">
                        {language === 'pl' ? 'Warsztat (opcjonalnie)' : 'Workshop (optional)'}
                    </label>
                    <input
                        type="text"
                        id="workshop_attended"
                        name="workshop_attended"
                        value={formData.workshop_attended}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-soft-gray rounded focus:ring-2 focus:ring-warm-sage focus:border-warm-sage transition-all"
                        placeholder={language === 'pl' ? 'Szycie dla Początkujących' : 'Sewing for Beginners'}
                    />
                </div>

                {/* Review Text */}
                <div>
                    <label htmlFor="review_text" className="block text-sm font-medium text-charcoal mb-2">
                        {language === 'pl' ? 'Twoja Opinia' : 'Your Review'} *
                    </label>
                    <textarea
                        id="review_text"
                        name="review_text"
                        value={formData.review_text}
                        onChange={handleChange}
                        required
                        rows={4}
                        className="w-full px-4 py-3 border border-soft-gray rounded focus:ring-2 focus:ring-warm-sage focus:border-warm-sage transition-all resize-none"
                        placeholder={language === 'pl' ? 'Podziel się swoim doświadczeniem... ✨' : 'Share your experience... ✨'}
                    />
                </div>

                {/* Submit Button */}
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-8 py-4 bg-warm-sage text-cream-white rounded uppercase tracking-widest text-sm font-medium transition-all duration-300 hover:bg-rich-chocolate hover:shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <span>{isSubmitting ? (language === 'pl' ? 'Wysyłanie...' : 'Submitting...') : (language === 'pl' ? 'Wyślij Opinię' : 'Submit Review')}</span>
                    <Send size={18} />
                </button>
            </form>
        </div>
    );
};

export default ReviewForm;
